import { keepPreviousData } from '@tanstack/react-query';
import { FC, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import {
  DirectoryHeader,
  EMPTY_PAGE,
  InfoIconUrl,
  Main,
  Padding,
  Stack,
  SystemClassifierFilterKey,
  TotalEntries,
  useDirectory,
  useSystemClassifierTranslations,
  useSystemClassifiersQuery,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './SystemClassifierDirectory.translations.json';
import { FooterPagination } from '../classifier-mapping/ClassifierMappingDirectory.styles';
import { Flex } from 'antd';
import { SystemClassifierDirectoryTable } from './SystemClassifierDirectoryTable';
import { Helmet } from 'react-helmet-async';

const FILTER_KEYS: SystemClassifierFilterKey[] = [
  'code',
  'name',
  'description',
  'userManaged',
];

const { addTranslations, useTranslation } = i18nNsFactory(
  'system-classifier-directory',
);
addTranslations(translations);

export const SystemClassifierDirectory: FC = () => {
  const { t } = useTranslation();
  const { translateSystemClassifier } = useSystemClassifierTranslations();

  const {
    headerProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialSort: [{ id: 'code', desc: false }],
  });

  const { data: { content, totalElements } = EMPTY_PAGE, isFetching } =
    useSystemClassifiersQuery(queryParameters, {
      placeholderData: keepPreviousData,
    });
  const translatedContent = useMemo(
    () => content.map(translateSystemClassifier),
    [content, translateSystemClassifier],
  );

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <Main>
        <Main.Col>
          <Main.Content padding={false} id="system-classifier-content">
            <ContentWrapper>
              <DirectoryHeader
                title={t('title')}
                infoIconUrl={InfoIconUrl.SYSTEM_CLASSIFIER_DIRECTORY}
                {...headerProps}
                onSearch={undefined}
              />
              <Padding bottom horizontal>
                <Flex vertical gap="small">
                  <SystemClassifierDirectoryTable
                    data={translatedContent}
                    isLoading={isFetching}
                    {...tableProps}
                  />
                </Flex>
              </Padding>
            </ContentWrapper>
          </Main.Content>
          <Main.Footer>
            {!isFetching && <TotalEntries count={totalElements} />}
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
      </Main>
    </>
  );
};

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  margin-right: auto;
  margin-left: auto;
`;
